// C:\Projects\texe\texe-web\src\pages\Home.tsx
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { getSavLikeLeaderboard, getMyCurrentRanks } from "../lib/api";
import { useAuth } from "../context/AuthProvider";
import { useGlobalPostInteractions } from "../context/PostInteractionsProvider";
import type { SavLikePeriod, SavLikeEntry, SavLikeMyRankData } from "../types/savlike";
import PageContainer from "../components/PageContainer";
import RichText from "../components/RichText";
import "./Home.css";

const PERIOD_LABELS: Record<SavLikePeriod, string> = {
  daily: "📅 Günlük",
  weekly: "📆 Haftalık",
  monthly: "📊 Aylık",
  yearly: "🏆 Yıllık",
};

export default function Home() {
  const { user } = useAuth();
  const { toggleLike, batchLoadInteractions, getInteraction } = useGlobalPostInteractions();
  const [period, setPeriod] = useState<SavLikePeriod>("yearly"); // Start with yearly to show more content
  const [entries, setEntries] = useState<SavLikeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [displayCount, setDisplayCount] = useState(25);
  const [myRankInfo, setMyRankInfo] = useState<SavLikeMyRankData | null>(null);
  const [animatingIds] = useState<Set<string>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);

  // Verileri yükle
  useEffect(() => {
    let isActive = true;

    const loadData = async () => {
      try {
        setLoading(true);
        const response = await getSavLikeLeaderboard(period, 100);

        if (!isActive) return;

        setEntries(response.items);

        // Load like states using global context
        const postIds = response.items
          .map((entry) => typeof entry.postId === "string" ? entry.postId : (entry.postId as any)?._id)
          .filter(Boolean);
        
        if (postIds.length > 0) {
          await batchLoadInteractions(postIds);
        }
      } catch (error) {
        if (!isActive) return;
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isActive = false;
    };
  }, [period]);

  // Kullanıcının rank bilgisini yükle
  useEffect(() => {
    const loadMyRankInfo = async () => {
      try {
        const response = await getMyCurrentRanks();
        setMyRankInfo(response.periods);
      } catch (error) {
        console.error("Rank bilgisi yükleme hatası:", error);
      }
    };

    if (user) {
      loadMyRankInfo();
    }
  }, [user]);

  const handleLoadMore = () => {
    setDisplayCount((prev) => Math.min(prev + 25, entries.length));
  };

  const getPostId = (entry: SavLikeEntry): string | null => {
    return typeof entry.postId === "string" ? entry.postId : (entry.postId as any)?._id || null;
  };

  const getUsername = (entry: SavLikeEntry): string => {
    const userId = entry.userId;
    return typeof userId === "object" && userId ? (userId as any).username || "Anonim" : "Anonim";
  };

  const getAvatarUrl = (entry: SavLikeEntry): string | null => {
    const userId = entry.userId;
    if (typeof userId === "object" && userId) {
      const url = (userId as any).avatarUrlSmall || (userId as any).avatarUrl;
      return url || null;
    }
    return null;
  };

  // Get user initial for fallback avatar
  const getUserInitial = (username: string): string => {
    return username.charAt(0).toUpperCase();
  };

  // Avatar error handler
  const handleAvatarError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    img.style.display = 'none';
  };

  const getPostText = (entry: SavLikeEntry): string => {
    const postId = entry.postId;
    if (typeof postId === "object" && postId) {
      return (postId as any).text || "[Post silinmiş]";
    }
    return "[Post silinmiş]";
  };

  const getTotalStars = (entry: SavLikeEntry): number => {
    const userId = entry.userId;
    if (typeof userId === "object" && userId) {
      return (userId as any).totalStars || 0;
    }
    return 0;
  };

  // Medal emoji
  const getMedal = (rank: number) => {
    if (rank === 1) return "🥇";
    if (rank === 2) return "🥈";
    if (rank === 3) return "🥉";
    return `#${rank}`;
  };

  // Card class'ı (rank'e göre)
  const getCardClass = (rank: number, isAnimating: boolean) => {
    let baseClass = "savlike-card";
    if (rank === 1) baseClass += " savlike-card-gold";
    else if (rank === 2) baseClass += " savlike-card-silver";
    else if (rank === 3) baseClass += " savlike-card-bronze";
    if (isAnimating) baseClass += " savlike-card-animating";
    return baseClass;
  };

  // Like/Unlike handler with optimistic update
  const handleToggleLike = async (postId: string) => {
    if (!user) {
      alert('Beğenmek için giriş yapmalısınız');
      return;
    }

    try {
      await toggleLike(postId);
      
      // Update entry's like count from global state
      setEntries((prevEntries) => {
        const newEntries = [...prevEntries];
        const entryIndex = newEntries.findIndex((e) => getPostId(e) === postId);
        
        if (entryIndex === -1) return prevEntries;
        
        const entry = { ...newEntries[entryIndex] };
        const interaction = getInteraction(postId);
        
        if (interaction) {
          entry.likes = interaction.likeCount;
          entry.score = entry.likes + (entry.comments * 0.2);
        }
        
        newEntries[entryIndex] = entry;
        return newEntries;
      });
    } catch (error) {
      console.error('Like error:', error);
    }
  };

  // Yorum sayfasına git
  const handleComment = (postId: string) => {
    if (!postId) return;
    window.location.href = `/p/${postId}`;
  };

  return (
    <PageContainer>
      <div className="savlike-container">
        {/* Header */}
        <div className="savlike-header">
          <h1 className="savlike-title">🏆 SavLike</h1>
          <p className="savlike-subtitle">Günlük Fikir Ligi</p>
        </div>

      {/* My Rank Info */}
      {user && myRankInfo && myRankInfo[period] && (
        <div className="savlike-myrank-container">
          <div className="savlike-myrank-card">
            <div className="savlike-myrank-left">
              <div className="savlike-myrank-label">Senin Sıran</div>
              <div className="savlike-myrank-number">
                {myRankInfo[period]!.rank > 0
                  ? `🏅 ${myRankInfo[period]!.rank}. sırada`
                  : "📝 Henüz sıralamada değilsin"}
              </div>
            </div>
            {myRankInfo[period]!.rank > 0 && (
              <div className="savlike-myrank-stats">
                <div className="savlike-myrank-stat">
                  <div className="savlike-myrank-stat-icon">❤️</div>
                  <div className="savlike-myrank-stat-text">{myRankInfo[period]!.likes}</div>
                </div>
                <div className="savlike-myrank-stat">
                  <div className="savlike-myrank-stat-icon">💬</div>
                  <div className="savlike-myrank-stat-text">{myRankInfo[period]!.comments}</div>
                </div>
                <div className="savlike-myrank-stat">
                  <div className="savlike-myrank-stat-icon">⭐</div>
                  <div className="savlike-myrank-stat-text">{myRankInfo[period]!.score.toFixed(1)}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Period Tabs */}
      <div className="savlike-tabs">
        {(["daily", "weekly", "monthly", "yearly"] as SavLikePeriod[]).map((p) => (
          <button
            key={p}
            className={`savlike-tab ${period === p ? "savlike-tab-active" : ""}`}
            onClick={() => setPeriod(p)}
          >
            {PERIOD_LABELS[p]}
          </button>
        ))}
      </div>

      {/* Leaderboard */}
      <div className="savlike-content" ref={containerRef}>
        {loading ? (
          <div className="savlike-loading">Yükleniyor...</div>
        ) : entries.length === 0 ? (
          <div className="savlike-empty">
            <div className="savlike-empty-text">Henüz gönderi yok</div>
            <div className="savlike-empty-subtext">İlk sen paylaş! 🚀</div>
          </div>
        ) : (
          <>
            {entries.slice(0, displayCount).map((entry) => {
              const postId = getPostId(entry);
              const username = getUsername(entry);
              const avatarUrl = getAvatarUrl(entry);
              const postText = getPostText(entry);
              const totalStars = getTotalStars(entry);
              const isTop3 = entry.rank <= 3;
              const isAnimating = animatingIds.has(entry._id);
              
              // Get like state from global context
              const interaction = postId ? getInteraction(postId) : null;
              const isLiked = interaction?.liked || false;
              const likeCount = interaction?.likeCount !== undefined ? interaction.likeCount : entry.likes;

              return (
                <div key={entry._id} className="savlike-card-wrapper">
                  {/* Pırıltı efekti - sadece Top 3 */}
                  {isTop3 && (
                    <div className="savlike-sparkle-container">
                      <div className="savlike-sparkle savlike-sparkle-1"></div>
                      <div className="savlike-sparkle savlike-sparkle-2"></div>
                      <div className="savlike-sparkle savlike-sparkle-3"></div>
                    </div>
                  )}

                  {/* Cam yansıması - sadece Top 3 */}
                  {isTop3 && (
                    <div className="savlike-glass-shine-container">
                      <div className={`savlike-glass-shine savlike-glass-shine-rank-${entry.rank}`}></div>
                    </div>
                  )}

                  <div className={getCardClass(entry.rank, isAnimating)}>
                    {/* Card Header */}
                    <div className="savlike-card-header">
                      <div className="savlike-card-header-left">
                        <div className={`savlike-rank-badge ${isAnimating ? "savlike-rank-badge-animating" : ""}`}>
                          <span className="savlike-rank-text">{getMedal(entry.rank)}</span>
                        </div>
                        <Link to={`/u/${username}`} className="savlike-username">
                          {username}
                          {totalStars > 0 && <span className="savlike-stars"> ⭐ {totalStars}</span>}
                        </Link>
                      </div>

                      {/* Delta badge */}
                      {entry.delta !== 0 && (
                        <div className={`savlike-delta-badge ${entry.delta > 0 ? "savlike-delta-up" : "savlike-delta-down"} ${isAnimating ? "savlike-delta-badge-animating" : ""}`}>
                          <span>{entry.delta > 0 ? "▲" : "▼"} {Math.abs(entry.delta)}</span>
                        </div>
                      )}

                      {/* Avatar */}
                      <Link to={`/u/${username}`} className="savlike-avatar-container">
                        {avatarUrl ? (
                          <img 
                            src={avatarUrl} 
                            alt={username} 
                            className="savlike-avatar"
                            onError={handleAvatarError}
                          />
                        ) : (
                          <div className="savlike-avatar-placeholder">
                            {getUserInitial(username)}
                          </div>
                        )}
                      </Link>
                    </div>

                    {/* Card Body */}
                    <div className="savlike-card-body">
                      {postId ? (
                        <Link to={`/p/${postId}`} className="savlike-post-text">
                          <RichText 
                            text={postText.length > 200 ? `${postText.slice(0, 200)}...` : postText} 
                            disableLinkify 
                          />
                        </Link>
                      ) : (
                        <div className="savlike-post-text savlike-post-text-disabled">
                          <RichText text={postText} />
                        </div>
                      )}
                    </div>

                    {/* Card Footer - Interactive */}
                    <div className="savlike-card-footer">
                      {/* Like Button */}
                      <button
                        className={`savlike-action-btn ${isLiked ? "savlike-action-btn-liked" : ""}`}
                        onClick={() => postId && handleToggleLike(postId)}
                        disabled={!postId || !user}
                        title={!user ? "Giriş yapmalısın" : isLiked ? "Beğenmekten vazgeç" : "Beğen"}
                      >
                        <span className="savlike-stat-icon">{isLiked ? "❤️" : "🤍"}</span>
                        <span className="savlike-stat-text">{likeCount}</span>
                      </button>

                      {/* Comment Button */}
                      <button
                        className="savlike-action-btn"
                        onClick={() => postId && handleComment(postId)}
                        disabled={!postId}
                        title="Yorum yap"
                      >
                        <span className="savlike-stat-icon">💬</span>
                        <span className="savlike-stat-text">{entry.comments}</span>
                      </button>

                      {/* Score Display */}
                      <div className={`savlike-stat savlike-stat-score ${isAnimating ? "savlike-stat-highlight" : ""}`}>
                        <span className="savlike-stat-icon">⭐</span>
                        <span className={`savlike-stat-text ${isAnimating ? "savlike-stat-text-highlight" : ""}`}>
                          {entry.score.toFixed(1)}
                          <span className="savlike-stat-formula">
                            {" "}({entry.likes}+{(entry.comments * 0.2).toFixed(1)})
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Load More Button */}
            {displayCount < entries.length && (
              <button className="savlike-load-more" onClick={handleLoadMore}>
                Daha Fazla Göster ({entries.length - displayCount} Post)
              </button>
            )}

            {/* End Message */}
            {displayCount >= entries.length && (
              <div className="savlike-end-message">
                🏁 Top {entries.length} gösteriliyor
              </div>
            )}
          </>
        )}
      </div>
      </div>
    </PageContainer>
  );
}
